import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { refreshToken, logout, clearError } from "../store/slices/authSlice";
import {
  CircularProgress,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const AuthGuard = ({ children }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, error } = useSelector((state) => state.auth);
  const [isChecking, setIsChecking] = useState(true);
  const [showSessionExpired, setShowSessionExpired] = useState(false);

  // Memoize public paths so they're stable
  const publicPaths = useMemo(
    () => ["/login", "/register", "/verify-email"],
    []
  );

  // Check for session expiration error
  useEffect(() => {
    if (
      error &&
      (error.includes("Token expired") ||
        error.includes("jwt expired") ||
        error.includes("Invalid token") ||
        error.includes("Password has been changed") ||
        error.includes("Your session has expired"))
    ) {
      setShowSessionExpired(true);
    }
  }, [error]);

  const handleSessionExpiredClose = () => {
    setShowSessionExpired(false);
    dispatch(clearError());
    dispatch(logout());
    navigate("/login", {
      state: {
        message: t(
          "auth.session.expiredRedirect",
          "Your session has expired. Please login again."
        ),
        type: "warning",
      },
    });
  };

  useEffect(() => {
    let refreshInterval;
    let isActive = true; // flag for cleanup

    const checkAndRefreshToken = async () => {
      try {
        if (!isActive) return;

        // Check if current path is public
        if (publicPaths.some((path) => location.pathname.startsWith(path))) {
          setIsChecking(false);
          return;
        }

        const storedRefreshToken = localStorage.getItem("refreshToken");
        const storedAccessToken = localStorage.getItem("accessToken");

        if (!storedAccessToken && !storedRefreshToken) {
          if (isActive) {
            dispatch(logout());
            navigate("/login");
            setIsChecking(false);
          }
          return;
        }

        if (storedRefreshToken && (!storedAccessToken || !token)) {
          await dispatch(refreshToken(storedRefreshToken)).unwrap();
        }

        if (isActive) {
          setIsChecking(false);
        }
      } catch (error) {
        console.error(
          t("errors.authCheckFailed", "Authentication check failed"),
          error
        );
        if (isActive) {
          dispatch(logout());
          navigate("/login");
          setIsChecking(false);
        }
      }
    };

    checkAndRefreshToken();

    if (token) {
      // Refresh token 1 minute before it expires (assuming 15 minute token life)
      refreshInterval = setInterval(() => {
        const currentRefreshToken = localStorage.getItem("refreshToken");
        if (currentRefreshToken) {
          dispatch(refreshToken(currentRefreshToken))
            .unwrap()
            .catch((error) => {
              console.error(
                t("errors.tokenRefreshFailed", "Token refresh failed"),
                error
              );
              if (isActive) {
                dispatch(logout());
                navigate("/login");
              }
            });
        }
      }, 14 * 60 * 1000); // 14 minutes
    }

    return () => {
      isActive = false;
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [dispatch, navigate, token, location.pathname, t, publicPaths]);

  if (isChecking) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>
          {t("common.loading", "Loading...")}
        </Typography>
      </Box>
    );
  }

  const isPublicRoute = publicPaths.some((path) =>
    location.pathname.startsWith(path)
  );

  return (
    <>
      {isPublicRoute || token ? children : null}

      {/* Session Expired Dialog */}
      <Dialog open={showSessionExpired} onClose={handleSessionExpiredClose}>
        <DialogTitle>
          {t("auth.session.expired", "Session Expired")}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              "auth.session.expiredMessage",
              "Your session has expired. Please login again to continue."
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSessionExpiredClose}
            variant="contained"
            color="primary"
          >
            {t("auth.session.loginAgain", "Login Again")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AuthGuard;
